import { useState } from "react";
import { Pressable } from "react-native";
import { Text, View, XStack } from "tamagui";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import { useAtom } from "jotai";
import { debounce } from "lodash";
import { HeaderButton } from "../../../components/design-system/Button/HeaderButton";
import { CloseIcon } from "../../../components/design-system/icons";
import { Input } from "../../../components/design-system";
import { RoutesList } from "../../../components/RideForm/components/RouteList";
import { rideAtom } from "../../../components/RideForm/atoms";
import { useSyncStravaRoutes } from "../../../features/Strava/hooks/useSyncStravaRoutes";
import { useApiQueryClient, useApiPost } from "../../../../lib/api";
import { Calendar, RefreshCcw } from "@tamagui/lucide-icons";
import { useAtomValue } from "jotai";
import { currentUserAtom } from "../../../../atoms/global";
import { RidesNavigatorParamList } from "../../../navigators/CreateARideNavigator";
import { BaseNavigatorParamList } from "../../../navigators/BaseNavigator";

export const RouteSelect = () => {
  const { top } = useSafeAreaInsets();
  const navigation = useNavigation<NavigationProp<RidesNavigatorParamList & BaseNavigatorParamList>>();
  const [ride, setRide] = useAtom(rideAtom);
  const currentUser = useAtomValue(currentUserAtom);
  const userId = currentUser?.id ?? "";

  const [searchTerm, setSearchTerm] = useState("");
  const setDebouncedSearch = debounce(setSearchTerm, 500);

  const queryClient = useApiQueryClient();
  const refreshQuery = () => queryClient.invalidateQueries({ queryKey: ["routes", userId] });

  const [syncStravaRoutes, isSyncing] = useSyncStravaRoutes(refreshQuery);
  const { mutate: syncStravaDates, isPending: isSyncingDates } = useApiPost<unknown, void>(
    "/api/v1/routes/sync-strava-dates",
    { onSuccess: refreshQuery }
  );

  const handleSkip = () => {
    navigation.navigate("RideInformation");
  };

  const handleRoutePress = (route: { node: { id: string; name: string | null } }) => {
    const routeName = route.node.name ?? "";
    setRide({
      ...ride,
      route: { id: route.node.id, name: routeName },
      name: routeName,
    });
    navigation.navigate("RideInformation");
  };

  return (
    <View flex={1} bg="$brandParchment" paddingTop={top}>
      <View px="$4" mb="$2">
        <XStack justifyContent="space-between" w="100%" alignItems="center">
          <HeaderButton onPress={() => navigation.goBack()}>
            <CloseIcon color="black" width={16} height={16} />
          </HeaderButton>
          <XStack alignItems="center" gap="$2">
            <HeaderButton onPress={() => syncStravaDates()} disabled={isSyncingDates}>
              <Calendar color="black" width={16} height={16} />
            </HeaderButton>
            <HeaderButton onPress={() => syncStravaRoutes()}>
              <RefreshCcw color="black" width={16} height={16} />
            </HeaderButton>
          </XStack>
        </XStack>
      </View>

      <Text
        fontSize={16}
        fontFamily="$heading"
        textTransform="uppercase"
        pl="$4"
        mb="$3"
      >
        Choose a Route
      </Text>

      <View px="$4" mb="$3">
        <Input placeholder="Search Routes" onChangeText={setDebouncedSearch} />
        <Pressable onPress={handleSkip}>
          <Text
            fontFamily="$heading"
            fontSize={12}
            mt="$2"
            textAlign="right"
            color="black"
          >
            I don't have a route →
          </Text>
        </Pressable>
      </View>

      <RoutesList
        profileId={userId}
        searchTerm={searchTerm}
        isSyncing={isSyncing}
        onRefresh={refreshQuery}
        onRoutePress={handleRoutePress}
      />
    </View>
  );
};
